import { backendClient } from '../lib/api/backendClient';
import type { PlaylistItem } from '../interfaces/playlists';
import type { Album } from '../interfaces/albums';
import type { Track } from '../interfaces/track';
import type { User } from '../interfaces/user';

type RawPlaylistItem = Record<string, unknown>;

type CachedTracksResponse = {
  items?: RawPlaylistItem[] | null;
  totalPersisted?: number | null;
};

type TracksPageResponsePayload = {
  items?: RawPlaylistItem[] | null;
  nextCursor?: string | null;
  overlap?: boolean;
  totalFetched?: number;
  sessionId?: string | null;
  commit?: boolean;
  persistedCount?: number;
  totalPersisted?: number;
};

type FavoriteTrackRecord = {
  payload?: RawPlaylistItem | null;
};

export type FetchTracksPageResponse = {
  items: PlaylistItem[];
  nextCursor: string | null;
  overlap: boolean;
  totalFetched: number;
  sessionId: string | null;
  commit: boolean;
  persistedCount: number;
  totalPersisted?: number;
};

export type SyncTracksResponse = {
  totalFetched: number;
  totalUpserted: number;
  items: FavoriteTrackRecord[];
};

const DEFAULT_USER: User = {
  display_name: '',
  external_urls: { spotify: '' },
  href: '',
  id: '',
  images: [
    { url: '', height: 0, width: 0 },
    { url: '', height: 0, width: 0 }
  ],
  type: '',
  uri: '',
  followers: { href: null, total: 0 },
  country: '',
  product: '',
  explicit_content: { filter_enabled: false, filter_locked: false },
  email: ''
};

const sanitizePayload = (payload: unknown): RawPlaylistItem => {
  if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
    return payload as RawPlaylistItem;
  }
  return {};
};

const toIsoTimestamp = (value: unknown): string => {
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) {
      return new Date(parsed).toISOString();
    }
  }
  return new Date().toISOString();
};

const coerceBoolean = (value: unknown): boolean => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  return false;
};

const isValidUser = (value: unknown): value is User => {
  if (!value || typeof value !== 'object') return false;
  const maybeUser = value as Partial<User>;
  return (
    typeof maybeUser.id === 'string' &&
    typeof maybeUser.uri === 'string' &&
    typeof maybeUser.type === 'string'
  );
};

const toPlaylistItem = (payload: unknown): PlaylistItem | null => {
  const record = sanitizePayload(payload);
  const track = record.track as Track;
  if (!track || typeof track !== 'object') {
    return null;
  }

  const addedBy = isValidUser(record.added_by) ? (record.added_by as User) : DEFAULT_USER;

  const sanitizedTrack: Track = {
    ...track,
    available_markets: [],
    album: (() => {
      const album = track.album;
      if (!album || typeof album !== 'object') {
        return album as Album;
      }
      const images = Array.isArray(album.images) ? album.images.slice(0, 1) : [];
      return {
        ...album,
        images
      };
    })()
  };

  const playlistItem: PlaylistItem = {
    added_at: toIsoTimestamp(record.added_at),
    added_by: addedBy,
    is_local: coerceBoolean(record.is_local),
    primary_color:
      typeof record.primary_color === 'string' ? record.primary_color : '',
    track: sanitizedTrack
  };

  return playlistItem;
};

const deserializePlaylistItems = (records: RawPlaylistItem[] | null | undefined): PlaylistItem[] => {
  if (!Array.isArray(records) || records.length === 0) {
    return [];
  }
  const items: PlaylistItem[] = [];
  for (const record of records) {
    const playlistItem = toPlaylistItem(record);
    if (playlistItem) {
      items.push(playlistItem);
    }
  }
  return items;
};

const buildAuthHeaders = (accessToken: string) => ({
  Authorization: `Bearer ${accessToken.trim()}`
});

export const libraryTracksService = {
  async fetchCachedTracks(options: {
    spotifyUserId: string;
    accessToken: string;
    limit?: number;
    offset?: number;
  }): Promise<{ items: PlaylistItem[]; totalPersisted: number }> {
    const headers = buildAuthHeaders(options.accessToken);
    const params: Record<string, string | number> = {
      spotifyUserId: options.spotifyUserId
    };

    if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
      const normalizedLimit = Math.trunc(options.limit);
      if (normalizedLimit > 0) {
        params.limit = normalizedLimit;
      }
    }

    if (typeof options.offset === 'number' && Number.isFinite(options.offset)) {
      const normalizedOffset = Math.trunc(options.offset);
      if (normalizedOffset >= 0) {
        params.offset = normalizedOffset;
      }
    }

    const { data } = await backendClient.get<CachedTracksResponse>('/library/liked-tracks', {
      params,
      headers
    });

    const items = deserializePlaylistItems(data?.items ?? []);
    const totalPersisted =
      typeof data?.totalPersisted === 'number' && Number.isFinite(data.totalPersisted)
        ? data.totalPersisted
        : items.length;

    return { items, totalPersisted };
  },

  async fetchTracksPage(options: {
    spotifyUserId: string;
    accessToken: string;
    after?: string | null;
    limit?: number;
    sessionId?: string | null;
  }): Promise<FetchTracksPageResponse> {
    const headers = buildAuthHeaders(options.accessToken);
    const params: Record<string, string | number> = {
      spotifyUserId: options.spotifyUserId
    };

    if (typeof options.after === 'string' && options.after.trim().length > 0) {
      params.after = options.after;
    }

    if (typeof options.sessionId === 'string' && options.sessionId.trim().length > 0) {
      params.sessionId = options.sessionId;
    }

    if (typeof options.limit === 'number' && Number.isFinite(options.limit)) {
      const normalizedLimit = Math.trunc(options.limit);
      if (normalizedLimit > 0) {
        params.limit = Math.min(normalizedLimit, 50);
      }
    }

    const { data } = await backendClient.get<TracksPageResponsePayload>(
      '/library/liked-tracks/page',
      {
        params,
        headers
      }
    );

    const items = deserializePlaylistItems(data?.items ?? []);
    const nextCursor =
      typeof data?.nextCursor === 'string' && data.nextCursor.trim().length > 0
        ? data.nextCursor
        : null;
    const overlap = Boolean(data?.overlap);
    const totalFetched =
      typeof data?.totalFetched === 'number' && Number.isFinite(data.totalFetched)
        ? data.totalFetched
        : items.length;
    const sessionId =
      typeof data?.sessionId === 'string' && data.sessionId.trim().length > 0
        ? data.sessionId
        : null;
    const commit = Boolean(data?.commit);
    const persistedCount =
      typeof data?.persistedCount === 'number' && Number.isFinite(data.persistedCount)
        ? data.persistedCount
        : 0;

    const totalPersisted =
      typeof data?.totalPersisted === 'number' && Number.isFinite(data.totalPersisted)
        ? data.totalPersisted
        : undefined;

    return {
      items,
      nextCursor,
      overlap,
      totalFetched,
      sessionId,
      commit,
      persistedCount,
      ...(typeof totalPersisted === 'number' ? { totalPersisted } : {})
    };
  },

  async syncTracks(options: {
    spotifyUserId: string;
    accessToken: string;
    pageLimit?: number;
  }): Promise<{ summary: SyncTracksResponse; tracks: PlaylistItem[] }> {
    const headers = buildAuthHeaders(options.accessToken);
    const payload: Record<string, string | number> = {
      spotifyUserId: options.spotifyUserId
    };

    if (typeof options.pageLimit === 'number' && options.pageLimit > 0) {
      payload.pageLimit = Math.min(Math.floor(options.pageLimit), 50);
    }

    const { data } = await backendClient.post<SyncTracksResponse>(
      '/library/liked-tracks/sync',
      payload,
      { headers }
    );

    const playlistItems =
      Array.isArray(data?.items) && data.items.length > 0
        ? data.items
            .map((record) => toPlaylistItem(record?.payload))
            .filter((item): item is PlaylistItem => item !== null)
        : [];

    return {
      summary: data,
      tracks: playlistItems
    };
  }
};

export default libraryTracksService;
